/* eslint-disable camelcase */
import React, { useEffect, useMemo } from "react";
import { Spin, Form, Divider } from "antd";
import { isValidPhoneNumber } from "react-phone-number-input";

import { MyAccountSubModule } from "@companion-core/shared/app/Interfaces/myAccount";
import { useMemberStore } from "@companion-core/shared/app/Store/memberStore";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { Button } from "@companion-core/shared/app/Components/Button";
import { JPInputPhone } from "@companion-core/shared/app/Components/Inputs/JPInputPhone/JPInputPhone";
import { getModule } from "@companion-core/shared/app/Utils/config";
import { JPInput } from "@companion-core/shared/app/Components/Inputs";
import { JPInputSelect } from "@companion-core/shared/app/Components/Inputs/JPInputSelect";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";
import { lockScroll, unlockScroll } from "@companion-core/web/src/Utilities/scroll";
import { ValidationBanner } from "@companion-core/shared/app/Components/ValidationBanner";
import { PersonalInfoConfigField, PhoneInfos } from "./PersonalInformation.model";
import { usePersonalInformation } from "./hooks/personalInformation";

import "@companion-core/web/src/Assets/Styles/scss/personalInformation.scss";

export const PersonalInformation = () => {
  const {
    t,
    isMemberFetching,
    data,
    personalInformationConfig,
    phoneDisplayed,
    setPhoneDisplayed,
    availablePhonesField,
    registerValidation,
    countriesOptions,
    organizationSizesOptions,
    organizationOptions,
    validateAllFields,
    onSubmitForm,
    isPending,
  } = usePersonalInformation();
  const { validationStatus, validationMessage } = useMemberStore();
  const [form] = Form.useForm();
  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const myAccountModule = getModule("my-account");

  const enabledModulesCount = useMemo(
    () =>
      Object.entries(myAccountModule?.modules)?.filter(
        (module) => !!(module[1] as MyAccountSubModule)?.enabled,
      ).length,
    [myAccountModule],
  );
  const showTitle = useMemo(
    () => isMobileSize || enabledModulesCount <= 1,
    [isMobileSize, enabledModulesCount],
  );

  useEffect(() => {
    if (data) {
      const phones = availablePhonesField.flatMap((availablePhoneField) =>
        data[availablePhoneField]
          ? { type: availablePhoneField, phone: data[availablePhoneField] }
          : [],
      );
      setPhoneDisplayed(phones as PhoneInfos[]);
    }
    if (isMobileSize) {
      lockScroll();
    }

    return () => {
      if (isMobileSize) unlockScroll();
    };
  }, [isMobileSize]);

  return isMemberFetching ? (
    <Spin className="personal-information__spin" size="large" />
  ) : (
    <div className="my-account__section personal-information">
      {showTitle && (
        <div
          data-testid="personal-information__title"
          className="my-account__section-title super-title-3 personal-information__title">
          {t("my-account.personal-information.title")}
        </div>
      )}

      <div className="my-account__section-content personal-information__section">
        <ValidationBanner
          testID="personal-information__validation"
          className="my-account__validation-banner"
          visible={!!validationStatus}
          text={validationMessage ?? ""}
          type={validationStatus ?? "success"}
          autoFadeOut
          fadeOutDelay={10000}
        />
        <Form
          name="personal-informations"
          form={form}
          onFinish={onSubmitForm}
          onFinishFailed={() => validateAllFields()}>
          <div
            className="body-text-3 personal-information__section-title"
            data-testid="personal-information__section-title">
            {t("my-account.informations.phones.title")}
          </div>
          {phoneDisplayed.map((field) => {
            return (
              <div key={field.type} className="personal-information__phone">
                {!personalInformationConfig?.forms?.phones?.find(
                  (entry: { type: string; required: boolean }) => entry.type === field.type,
                )?.required && (
                  <a
                    className="personal-information__phone-delete body-text-6"
                    data-testid={`personal-information__phone-delete-${field.type}`}
                    onClick={() =>
                      setPhoneDisplayed(phoneDisplayed.filter((entry) => field.type !== entry.type))
                    }>
                    {t("my-account.links.phone.delete")}
                  </a>
                )}
                <Form.Item
                  name={field.type}
                  initialValue={field.phone}
                  rules={[
                    {
                      required:
                        personalInformationConfig?.forms?.phones?.find(
                          (entry: { type: string; required: boolean }) => entry.type === field.type,
                        )?.required ?? false,
                      message: "",
                    },
                    {
                      validator: (_, value) =>
                        isValidPhoneNumber(value) ? Promise.resolve() : Promise.reject(new Error()),
                      message: "",
                    },
                  ]}>
                  <JPInputPhone
                    label={t(`my-account.form.inputs.phone.${field.type}.label`)}
                    name={field.type}
                    testID={`personal-information__phone-${field.type}`}
                    required={true}
                    registerValidation={registerValidation}
                  />
                </Form.Item>
              </div>
            );
          })}

          <div className="personal-information__phone-add-container">
            {personalInformationConfig?.forms?.phones
              ?.filter(
                (entry: PersonalInfoConfigField) =>
                  phoneDisplayed.find((value) => value.type === entry.type) === undefined,
              )
              .map((value: PersonalInfoConfigField) => (
                <div className="personal-information__phone-add" key={value.type}>
                  <div
                    className="personal-information__phone-add-clickable"
                    onClick={() =>
                      setPhoneDisplayed([...phoneDisplayed, { type: value.type, phone: "" }])
                    }>
                    <div
                      className="personal-information__phone-add-background"
                      data-testid={`personal-information__phone-add-icon-${value.type}`}>
                      <FontIcon
                        className="personal-information__phone-add-icon"
                        name="JP-Plus"
                        size={18}
                      />
                    </div>
                    <a
                      data-testid={`personal-information__phone-add-link-${value.type}`}
                      className="link-1 personal-information__phone-add-link">
                      {t(`my-account.informations.phone.links.add.${value.type}`)}
                    </a>
                  </div>
                </div>
              ))}
          </div>

          {personalInformationConfig?.forms?.addresses?.length && (
            <div className="margin-bottom-24">
              <Divider />
              <div className="body-text-3 personal-information__section-title">
                {t("my-account.informations.addresses.title")}
              </div>
              <div className="margin-bottom-8">
                {data?.addresses.map((address) => {
                  const addressConfig = personalInformationConfig.forms.addresses.find(
                    (value: PersonalInfoConfigField) => address.type === value.type,
                  );
                  if (!addressConfig) return <></>;
                  return (
                    <div key={address.type} className="margin-bottom-24">
                      {data?.addresses && data.addresses.length > 1 && (
                        <div
                          className="body-text-5 personal-information__section-title"
                          data-testid={`personal-information__${address.type}-title`}>
                          {t(`my-account.addresses.label.${address.type}`)}
                        </div>
                      )}
                      <Form.Item
                        name={`${address.type}-street`}
                        initialValue={address.street}
                        rules={[{ required: addressConfig?.required, message: "" }]}>
                        <JPInput
                          label={t("my-account.form.inputs.addresses.street.label")}
                          name={`${address.type}-street`}
                          testID={`personal-information__${address.type}-street`}
                          type="text"
                          required={addressConfig?.required}
                          disabled={addressConfig?.disabled}
                          registerValidation={registerValidation}
                        />
                      </Form.Item>
                      <div className="personal-information__address margin-top-8 margin-bottom-8">
                        <Form.Item
                          name={`${address.type}-city`}
                          initialValue={address.city}
                          rules={[{ required: addressConfig?.required, message: "" }]}>
                          <JPInput
                            label={t("my-account.form.inputs.addresses.city")}
                            name={`${address.type}-city`}
                            testID={`personal-information__${address.type}-city`}
                            type="text"
                            required={addressConfig?.required}
                            disabled={addressConfig?.disabled}
                            registerValidation={registerValidation}
                          />
                        </Form.Item>
                        <div className="personal-information__address-spacing" />
                        <Form.Item
                          name={`${address.type}-zip_code`}
                          initialValue={address.zip_code}
                          rules={[{ required: addressConfig?.required, message: "" }]}>
                          <JPInput
                            label={t("my-account.form.inputs.addresses.zip-code")}
                            name={`${address.type}-zip_code`}
                            testID={`personal-information__${address.type}-zip-code`}
                            type="text"
                            required={addressConfig?.required}
                            disabled={addressConfig?.disabled}
                            registerValidation={registerValidation}
                          />
                        </Form.Item>
                      </div>
                      <Form.Item
                        name={`${address.type}-country_code`}
                        initialValue={address?.country_code?.toUpperCase()}
                        rules={[{ required: addressConfig?.required, message: "" }]}>
                        <JPInputSelect
                          options={countriesOptions}
                          name={`${address.type}-country_code`}
                          initialValue={address?.country_code?.toUpperCase()}
                          label={t("my-account.form.inputs.addresses.country")}
                          testID={`personal-information__${address.type}-country-code`}
                          required={addressConfig?.required}
                          disabled={addressConfig?.disabled}
                          registerValidation={registerValidation}
                        />
                      </Form.Item>
                    </div>
                  );
                })}
              </div>
              {(!data?.addresses || data?.addresses.length === 0) && (
                <span data-testid="personal-information__no-addresses-link body-text-6">
                  {t("my-account.informations.addresses.complete.pre")}{" "}
                  <a className="link-1">{t("my-account.informations.addresses.complete.link")}</a>
                </span>
              )}
            </div>
          )}

          {personalInformationConfig.forms.organization && (
            <>
              <Divider />
              <div className="body-text-3 personal-information__section-title">
                {t("my-account.informations.organization.title")}
              </div>
              {personalInformationConfig.forms.organization?.name && (
                <Form.Item name={"organization-name"} initialValue={data?.organization.name}>
                  <JPInput
                    name="organization-name"
                    label={t("my-account.form.inputs.organization-name")}
                    testID="personal-information__organization-name"
                    type="text"
                  />
                </Form.Item>
              )}

              {personalInformationConfig.forms.organization?.industry && (
                <Form.Item
                  name={"organization-industry"}
                  initialValue={data?.organization.industry}>
                  <JPInputSelect
                    label={t("my-account.form.inputs.organization-industry")}
                    options={organizationOptions}
                    name="organization-industry"
                    initialValue={data?.organization.industry as string}
                    testID="personal-information__organization-industry"
                  />
                </Form.Item>
              )}

              {personalInformationConfig.forms.organization?.size && (
                <Form.Item name={"organization-size"} initialValue={data?.organization.size}>
                  <JPInputSelect
                    label={t("my-account.form.inputs.organization-size")}
                    options={organizationSizesOptions}
                    initialValue={data?.organization.size as string}
                    name="organization-size"
                    testID="personal-information__organization-size"
                  />
                </Form.Item>
              )}
            </>
          )}

          <Form.Item>
            <Button
              classNames={["personal-information__submit"]}
              testID="personal-information__cta"
              label={t("my-account.informations.form.buttons.submit")}
              loading={isPending}
              htmlType="submit"
            />
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};
